<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { getDisplayDateTime } from "@/modules/common";
import UserAvatar from "@/components/UserAvatar.vue";
import { onBeforeMount, ref } from "vue";
import { useMate } from "@/stores/mate";
import { Mate } from "@/types/entity";
import { useRoute, useRouter } from "vue-router";
import { useChatSession } from "@/stores/chatSession";
import {
  PUBLIC_MODE_URL,
  PRIVATE_MODE_URL,
} from "@/components/mobile/chat/constants";
import { showLoadingToast } from "vant";
import { WaitingSessionCreationInstance } from "@/components/mobile/chat/WaitingSessionCreation.vue";
import i18n from "@/locales";

const mateStore = useMate();
const chatSession = useChatSession();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const mate = ref<Mate | null>(null);
const waitingRef = ref<WaitingSessionCreationInstance | null>(null);
const isPrivate = ref<0 | 1 | null>(null); // 0-公开；1-私密

onBeforeMount(async () => {
  loading.value = true;
  try {
    const uuid = route.params.uuid as string;
    mate.value = await mateStore.getOrFetchMate(uuid);
  } finally {
    loading.value = false;
  }
});

const onCreatePublicSession = async () => {
  if (mate.value == null) return;
  const loading = showLoadingToast({
    message: i18n.global.t("Message.settingChatEnvironment"),
  });
  try {
    const session = await chatSession.createPublicSession(mate.value.id);
    await chatSession.goChat(router, mate.value.uuid, session.id, true);
  } finally {
    loading.close();
  }
};

const onCreateSession = async () => {
  if (mate.value == null) return;
  if (isPrivate.value == null) return;
  if (isPrivate.value === 0) {
    await onCreatePublicSession();
  } else {
    await waitingRef.value?.createPrivateSession(mate.value, "replace");
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <threads-nav :show-center="false" show-back />
    <div v-if="loading" class="flex items-center justify-center">
      <div class="loading loading-sm"></div>
    </div>
    <div v-else class="p-4 overflow-y-auto flex flex-col gap-2 items-center">
      <div class="text-base-content/50">
        {{ getDisplayDateTime(new Date(new Date().toUTCString())) }}
      </div>
      <div class="bg-neutral p-4 rounded-lg text-base-content/50">
        {{ $t("Chats.createChat") }}
        <span class="text-base-content font-bold">{{ mate!.name }}</span>
        {{ $t("Chats.createChatHalf") }}
      </div>
      <div class="w-full flex flex-col text-base text-base-content/50 gap-2">
        <span>{{ $t("Chats.initiateChat") }}</span>
        <div class="w-full flex flex-col gap-2 items-center">
          <div class="w-full grid grid-cols-2 gap-2">
            <div
              :class="{ 'text-primary': isPrivate === 0 }"
              class="flex flex-col gap-1 w-full"
              @click="isPrivate = 0"
            >
              <user-avatar
                :avatar-url="PUBLIC_MODE_URL"
                :class="{ grayscale: isPrivate !== 0 }"
                class="w-full"
                high-quality
              />
              <div class="flex flex-col items-center">
                <span
                  :class="{
                    'text-primary': isPrivate === 0,
                    'text-base-content': isPrivate !== 0,
                  }"
                  >{{ $t("Chats.publicChat") }}</span
                >
                <div class="flex flex-col text-xs gap-0.5">
                  <div>· {{ $t("Chats.publicOne") }}</div>
                  <div>· {{ $t("Chats.publicTwo") }}</div>
                  <div>· {{ $t("Chats.publicThree") }}</div>
                </div>
              </div>
            </div>
            <div
              :class="{ 'text-primary': isPrivate === 1 }"
              class="flex flex-col gap-1 w-full"
              @click="isPrivate = 1"
            >
              <user-avatar
                :avatar-url="PRIVATE_MODE_URL"
                :class="{ grayscale: isPrivate !== 1 }"
                class="w-full"
                high-quality
              />
              <div class="flex flex-col items-center">
                <span
                  :class="{
                    'text-primary': isPrivate === 1,
                    'text-base-content': isPrivate !== 1,
                  }"
                  >{{ $t("Chats.privateChat") }}</span
                >
                <div class="flex flex-col text-xs gap-0.5">
                  <div>· {{ $t("Chats.privateOne") }}</div>
                  <div>· {{ $t("Chats.privateTwo") }}</div>
                  <div>· {{ $t("Chats.privateThree") }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        :disabled="isPrivate == null"
        class="btn rounded-full btn-primary w-44 mt-5"
        @click="onCreateSession"
      >
        {{ $t("Buttons.confirm") }}
      </button>
    </div>
    <waiting-session-creation ref="waitingRef" />
  </div>
</template>
